import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import "./css/WorkPreferences.css";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;

export default function WorkPreferences() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");

  // Form fields
  const [isWorkWithinDistance, setIsWorkWithinDistance] = useState(false);
  const [workWithinDistance, setWorkWithinDistance] = useState("");
  const [notificationPreference, setNotificationPreference] = useState({
    email: false,
    sms: false,
    whatsapp: false,
  });

  // Translations
  const t = {
    en: {
      generalSettings: "General Settings",
      workPreferences: "Work Preferences",
      warningMessage: "Make any changes or leave it blank and save once to mark this section as completed!",
      receiveWorkWithinDistance: "Receive work within a fixed distance",
      distanceInKm: "Distance in Km",
      notificationPreferences: "Notification Preferences",
      email: "Email",
      whatsapp: "Whatsapp",
      sms: "SMS",
      save: "Save",
      saving: "Saving...",
      loading: "Loading",
      distanceRequired: "Please enter distance in Km",
    },
    ar: {
      generalSettings: "الإعدادات العامة",
      workPreferences: "تفضيلات العمل",
      warningMessage: "قم بإجراء أي تغييرات أو اتركها فارغة واحفظ مرة واحدة لتمييز هذا القسم كمكتمل!",
      receiveWorkWithinDistance: "استلام العمل ضمن مسافة ثابتة",
      distanceInKm: "المسافة بالكيلومتر",
      notificationPreferences: "تفضيلات الإشعارات",
      email: "البريد الإلكتروني",
      whatsapp: "الواتساب",
      sms: "رسالة نصية",
      save: "حفظ",
      saving: "جاري الحفظ...",
      loading: "جاري التحميل",
      distanceRequired: "يرجى إدخال المسافة بالكيلومتر",
    },
  }[lang];

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;
          
          // Set language preference
          if (profileData.language_preference) {
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          }

          setProfile(profileData);
          
          // Populate form fields
          setIsWorkWithinDistance(profileData.is_work_within_distance || false);
          setWorkWithinDistance(
            profileData.work_within_distance ? profileData.work_within_distance.toString() : ""
          );
          setNotificationPreference({
            email: profileData.notification_preference?.email || false,
            sms: profileData.notification_preference?.sms || false,
            whatsapp: profileData.notification_preference?.whatsapp || false,
          });
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        showToast(err.message || "Failed to load profile", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle save
  const handleSave = async () => {
    // Validate: if is_work_within_distance is true, work_within_distance is required
    if (isWorkWithinDistance && (!workWithinDistance || workWithinDistance.trim() === "")) {
      showToast(t.distanceRequired, "error");
      return;
    }

    // Validate distance is a number and >= 0
    if (isWorkWithinDistance && workWithinDistance) {
      const distanceNum = parseFloat(workWithinDistance);
      if (isNaN(distanceNum) || distanceNum < 0) {
        showToast(
          lang === "ar"
            ? "يجب أن تكون المسافة رقماً صحيحاً أكبر من أو يساوي الصفر"
            : "Distance must be a valid number greater than or equal to 0",
          "error"
        );
        return;
      }
    }

    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("is_work_within_distance", String(isWorkWithinDistance));
      
      if (isWorkWithinDistance && workWithinDistance) {
        formData.append("work_within_distance", workWithinDistance);
      }
      
      formData.append("notification_preference", JSON.stringify(notificationPreference));

      const response = await fetch(
        `${USER_BASE_URL}/${VERSION}/profile/edit`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "app-version": "1.0.0",
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (data && data.response_code === 200) {
        showToast(
          lang === "ar" ? "تم الحفظ بنجاح" : "Saved successfully",
          "success"
        );
        // Refresh page
        window.location.reload();
      } else {
        showToast(
          data?.message || (lang === "ar" ? "فشل الحفظ" : "Failed to save"),
          "error"
        );
        setSaving(false);
      }
    } catch (err) {
      console.error("Error saving work preferences:", err);
      showToast(
        err.message || (lang === "ar" ? "فشل الحفظ" : "Failed to save"),
        "error"
      );
      setSaving(false);
    }
  };

  // Handle notification preference change
  const handleNotificationChange = (type) => {
    setNotificationPreference((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // Loading screen
  if (loading) {
    return (
      <div className="work-preferences-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="work-preferences-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Page Header with Back Button */}
      <div className="work-preferences-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="work-preferences-page-title">{t.generalSettings}</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Main Content */}
      <div className="work-preferences-content">
        {/* Warning Banner */}
        {!profile.work_preferences_completed && (
          <div className="work-preferences-warning-banner">
            <p>{t.warningMessage}</p>
          </div>
        )}

        {/* Work Preferences Section */}
        <div className="work-preferences-section">
          <h2 className="section-title">{t.workPreferences}</h2>
          
          {/* Receive work within distance checkbox */}
          <div className="checkbox-container">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isWorkWithinDistance}
                onChange={(e) => setIsWorkWithinDistance(e.target.checked)}
                disabled={saving}
                className="checkbox-input"
              />
              <span className="checkbox-custom"></span>
              <span className="checkbox-text">{t.receiveWorkWithinDistance}</span>
            </label>
          </div>

          {/* Distance input - only show if checkbox is checked */}
          {isWorkWithinDistance && (
            <div className="distance-input-container">
              <label className="input-label">{t.distanceInKm}</label>
              <input
                type="number"
                className="distance-input"
                value={workWithinDistance}
                onChange={(e) => setWorkWithinDistance(e.target.value)}
                disabled={saving}
                min="0"
                placeholder="0"
              />
            </div>
          )}
        </div>

        {/* Notification Preferences Section */}
        <div className="work-preferences-section">
          <h2 className="section-title">{t.notificationPreferences}</h2>
          
          <div className="notification-checkboxes">
            {/* Email */}
            <div className="checkbox-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={notificationPreference.email}
                  onChange={() => handleNotificationChange("email")}
                  disabled={saving}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">{t.email}</span>
              </label>
            </div>

            {/* WhatsApp */}
            <div className="checkbox-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={notificationPreference.whatsapp}
                  onChange={() => handleNotificationChange("whatsapp")}
                  disabled={saving}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">{t.whatsapp}</span>
              </label>
            </div>

            {/* SMS */}
            <div className="checkbox-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={notificationPreference.sms}
                  onChange={() => handleNotificationChange("sms")}
                  disabled={saving}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">{t.sms}</span>
              </label>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          className="work-preferences-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="save-spinner"></span>
              {t.saving}
            </>
          ) : (
            t.save
          )}
        </button>
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="settings" lang={lang} />
    </div>
  );
}

